import { Fragment, FunctionComponent, useEffect } from "react";
import { Hero } from "@App/Components/Hero";
import { ContactUs } from "@App/Components/ContactUs";
import { TriangleDivider } from "@App/Components/TriangleDivider";
import { Stepper } from "@App/Components/Stepper/Stepper";
import { useServices } from "@App/hooks/api/useServices";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useHome } from "@App/hooks/useHome";
import { Loader } from "@App/Components/Loader";
import { ServiceStep } from "@App/Pages/Partner/Partials/ServiceStep";
import { SubServiceStep } from "@App/Pages/Partner/Partials/SubServiceStep";
import { Benefits } from "@App/Pages/Partner/Partials/Benefits";
import {
  ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored,
  ServiceIsWheelChangeOrServieIsWheelChangeTiresstored,
} from "@App/utils/conditionals/combinations";
import { SecondaryTireServiceStep } from "@App/Pages/Partner/Partials/SecondaryTireServiceStep";
import { PremiumServices } from "@App/types/enums";
import {
  ServiceIsAdditionalTireServices,
  ServiceIsWheelReplacement,
} from "@App/utils/conditionals/services";
import { LocationStepDefinition } from "@App/Components/molecules/whitelabel/home/stepDefinitions/LocationStepDefinition";
import { useLocationStepDefinition } from "@App/hooks/whitelabel/home/stepper/useLocationStepDefinition";
import { Step } from "@App/Components/Stepper/Step";

export type HomeProps = {
  embedded?: boolean;
  onSubmit: () => void;
};

export const Home: FunctionComponent<HomeProps> = ({ embedded = false, onSubmit }: HomeProps) => {
  const location = useLocation();

  const { t } = useTranslation();

  // step definitions
  const locationStepProps = useLocationStepDefinition(onSubmit);

  const { loading, error, services } = useServices();

  const {
    currentStep,
    setCurrentStep,
    togglePremiumService,
    togglePremiumServiceCarReplacementOrPickup,
    toggled,
    nextStep,
    organization,
    service,
    availableSubservices,
    availableComfortservices,
    availableSubservicesLoading,
    availableComfortservicesLoading,
    premiumServices,
    selectService,
    selectTireServiceTypeAndService,
    secondaryTireStepSelection,
    toggleSingularPremiumService,
    stepToOneAndResetOthers,
  } = useHome();

  useEffect(() => {
    if (Number(window.location.hash.substr(1)) > 0) {
      setCurrentStep(Number(window.location.hash.substr(1)));
    }
  }, [location]);

  //if service is available initially (ie. on browserback) or arrives async from the ?service= url param, set step to 1
  useEffect(() => {
    if (service && currentStep === 0) {
      stepToOneAndResetOthers();
    }
  }, [service]);

  //if only one service is available set service to that and skip selection step (this needs deps, because services are empty on initial pageload)
  useEffect(() => {
    if (services && services.length === 1 && !service) {
      selectService(services[0], true);
      setCurrentStep(1);
    }
  }, [services]);

  if (error) {
    console.log(error);
  }

  return (
    <Fragment>
      <Hero
        organization={organization}
        title={t("hero.title", { organization: organization.name })}
        subtitle={t("hero.subtitle", { organization: organization.name })}
        backgroundImage={
          organization.settings.hero.background_image_url ??
          "/images/brand/default-banner-darkened.jpg"
        }
        backgroundPosition={"bg-center"}
      />
      <div className="bg-white px-5 pt-5 container">
        <Stepper currentStep={currentStep} onChange={setCurrentStep}>
          <Step
            title={t("Waehlen Sie einen Service.")}
            subtitle={service && service.pivot.subtitle ? service.pivot.subtitle : null}
            completedTitle={"Service: "}
            completedGoBackLinkTitle={
              service
                ? service.pivot.display_name
                  ? service.pivot.display_name
                  : service.name
                : ""
            }
          >
            <ServiceStep
              service={service}
              services={services}
              loading={loading}
              error={error}
              selectService={selectService}
              organization={organization}
            />
          </Step>
          {availableSubservicesLoading && <Loader></Loader>}
          {ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored(service) &&
            !availableSubservicesLoading && (
              <Step
                title={"Sind die Reifen eingelagert?"}
                subtitle={null}
                completedTitle={"Sind die Reifen eingelagert?" + ": "}
                completedGoBackLinkTitle={secondaryTireStepSelection}
              >
                <SecondaryTireServiceStep
                  services={services}
                  selectService={selectTireServiceTypeAndService}
                  organization={organization}
                  secondary={true}
                />
              </Step>
            )}
          {ServiceIsAdditionalTireServices(service) &&
            (availableSubservices?.length > 0 || service == null) &&
            !availableSubservicesLoading && (
              <Step
                title={t("chooseSubServices")}
                subtitle={null}
                completedTitle={"Services: "}
                completedGoBackLinkTitle={`${premiumServices.length == 0 ? "Keine Auswahl" : ""} ${premiumServices.map((selected) => `${selected.name}`)}`}
              >
                <SubServiceStep
                  availableSubservices={availableSubservices.filter((subservice) =>
                    [
                      PremiumServices.WHEELCHANGECARACTIVEPTMS,
                      PremiumServices.STATIONARYBALANCING,
                      PremiumServices.WHEELASSEMBLYDISASSEMBLY,
                      PremiumServices.WHEELSETRETREIVAL,
                    ].includes(parseInt(subservice.id)),
                  )}
                  premiumServices={premiumServices}
                  toggled={toggled}
                  togglePremiumServiceCarReplacementOrPickup={
                    togglePremiumServiceCarReplacementOrPickup
                  }
                  togglePremiumService={togglePremiumService}
                  nextStep={nextStep}
                />
              </Step>
            )}
          {ServiceIsWheelReplacement(service) &&
            (availableSubservices?.length > 0 || service == null) &&
            !availableSubservicesLoading && (
              <Step
                title={t("chooseTireAmount")}
                subtitle={null}
                completedTitle={"Services: "}
                completedGoBackLinkTitle={`${premiumServices.length == 0 ? "Keine Auswahl" : ""} ${premiumServices.map((selected) => `${selected.name}`)}`}
              >
                <SubServiceStep
                  availableSubservices={availableSubservices.filter((subservice) =>
                    [
                      PremiumServices.ONETIREREPLACEMENT,
                      PremiumServices.TWOTIREREPLACEMENT,
                      PremiumServices.THREETIREREPLACEMENT,
                      PremiumServices.FOURTIREREPLACEMENT,
                    ].includes(parseInt(subservice.id)),
                  )}
                  premiumServices={premiumServices}
                  toggled={toggled}
                  togglePremiumServiceCarReplacementOrPickup={
                    togglePremiumServiceCarReplacementOrPickup
                  }
                  togglePremiumService={toggleSingularPremiumService}
                  nextStep={nextStep}
                />
              </Step>
            )}
          {ServiceIsWheelChangeOrServieIsWheelChangeTiresstored(service) &&
            (availableComfortservices?.length > 0 || service == null) &&
            !availableComfortservicesLoading && (
              <Step
                title={t("optionalSubservice")}
                subtitle={null}
                completedTitle={"Optionale Services: "}
                completedGoBackLinkTitle={`${premiumServices.length == 0 ? "Keine Auswahl" : ""} ${premiumServices.map((selected) => `${selected.name}`)}`}
              >
                <SubServiceStep
                  availableSubservices={availableComfortservices.filter((comfortservice) =>
                    [PremiumServices.WHEELHUBCLEANING].includes(parseInt(comfortservice.id)),
                  )}
                  premiumServices={premiumServices}
                  toggled={toggled}
                  togglePremiumServiceCarReplacementOrPickup={
                    togglePremiumServiceCarReplacementOrPickup
                  }
                  togglePremiumService={togglePremiumService}
                  nextStep={nextStep}
                  required={false}
                />
              </Step>
            )}
          <LocationStepDefinition {...locationStepProps} />
        </Stepper>
      </div>
      {!embedded && (
        <div className="bg-neutral-100">
          <TriangleDivider direction="left" background={"bg-white"} />
          <div className="px-5 py-5 container md:pb-20">
            <div className="md:grid md:grid-cols-[50%_50%]">
              <Benefits organization={organization} />
              <div className="pt-5 md:pt-0 md:ml-12">
                <ContactUs />
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};
